/* eslint-disable import/no-duplicates */
import React from 'react';
import PropTypes from 'prop-types';
import Chart from 'components/chart';
import Legend from 'components/legend';
import InteractionSurface from 'components/interaction-surface';
import StripeDefs from 'components/stripe-defs';
import TimeseriesGroup from 'components/timeseries-group';
import Tooltip from 'components/tooltip';
import {COLUMN_LAYOUT} from 'constants/layout-mode';
import {FULL_WIDTH_LAYOUT} from 'constants/layout-mode';
import {CLUSTERED_COLUMN_SERIES} from 'constants/series-type';
import {getTranslate} from 'utils/attribute-util';
import {getFilteredSeries, getParsedGroups} from 'utils/data-util';
import {
  getChartWidth,
  getColumnCenterX,
  getContentWidth,
  getContentX,
  getFullWidthX,
  getGutters,
} from 'utils/layout-util';
import {getNumTicks} from 'utils/tick-util';
import styles from './index.css';

export default class TimeseriesChart extends React.Component {
  static propTypes = {
    contentHeight: PropTypes.number,
    groups: PropTypes.array,
    gutters: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.objectOf(PropTypes.number),
    ]),
    layoutMode: PropTypes.oneOf([COLUMN_LAYOUT, FULL_WIDTH_LAYOUT]),
    legendSpacing: PropTypes.number,
    onHeightUpdate: PropTypes.func,
    width: PropTypes.number,
  };

  static defaultProps = {
    contentHeight: 144,
    groups: [
      {
        series: [
          {
            data: [],
          },
        ],
      },
    ],
    gutters: 0,
    layoutMode: COLUMN_LAYOUT,
    legendSpacing: 0,
    onHeightUpdate: () => null,
    width: 0,
  };

  constructor(props) {
    super(props);
    this.state = {
      height: props.contentHeight,
    };
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.state.height !== prevState.height) {
      this.props.onHeightUpdate(this.state.height);
    }
  }

  getContentPadding(groups, gutters, padding = 20) {
    // 20px of padding accommodates axis labels
    // (if the corresponding gutter is enabled).
    let leftPadding = 0;
    if (gutters.left && groups[0] && groups[0].label) {
      leftPadding = padding;
    }

    let rightPadding = 0;
    if (gutters.right && groups[1] && groups[1].label) {
      rightPadding = padding;
    }

    // Clustered columns require special handling:
    if (
      gutters.right &&
      groups[1] &&
      groups[1].series &&
      groups[1].series[0] &&
      groups[1].series[0].type === CLUSTERED_COLUMN_SERIES
    ) {
      rightPadding = padding;
    }

    return {
      left: leftPadding,
      right: rightPadding,
    };
  }

  getContentWidthMinusPadding(contentWidth, contentPadding) {
    const {left, right} = contentPadding;
    return contentWidth - left - right;
  }

  getContentY(hasHeader, legendRows, legendHeight, legendSpacing) {
    if (hasHeader) {
      return Math.round(legendHeight * legendRows + legendSpacing);
    }
    return 0;
  }

  getCurrentIndex(activeIndex, closestIndex, layoutMode) {
    return layoutMode === FULL_WIDTH_LAYOUT ? closestIndex : activeIndex;
  }

  getGuttersForGroups(gutters, groups) {
    const box = getGutters(gutters);
    if (box.top > 0) {
      // If the top gutter is greater than zero,
      // the legend should be visible. Mandate
      // a height of 28px for the legend.
      box.top = 28;
    }
    if (groups.length < 2) {
      // The right margin is only used when
      // there is more than one group, and
      // thus more than one vertical axis.
      box.right = 0;
    }
    return box;
  }

  getMaxItems(series) {
    return Math.max(...series.map((datum) => datum.data.length));
  }

  getTickRange(layoutMode, tickWidth, maxItems, contentWidthMinusPadding) {
    return layoutMode === FULL_WIDTH_LAYOUT
      ? contentWidthMinusPadding / maxItems
      : tickWidth;
  }

  getTickWidth(contentWidthMinusPadding, numTicksX) {
    return contentWidthMinusPadding / numTicksX;
  }

  getTooltipX(
    currentIndex,
    layoutMode,
    gutters,
    tickWidth,
    contentWidthMinusPadding,
    numTicksX,
    contentPadding,
  ) {
    const tooltipX =
      layoutMode === COLUMN_LAYOUT
        ? getColumnCenterX(tickWidth, currentIndex)
        : getFullWidthX(contentWidthMinusPadding, numTicksX, currentIndex);
    return tooltipX + gutters.left + contentPadding.left;
  }

  getTooltipY() {
    // TODO This is expected to get quite complex in future iterations.
    return 0;
  }

  hasHeader(gutters) {
    return Boolean(gutters && gutters.top);
  }

  hasLeftDivider(groups, gutters) {
    if (groups[0] && groups[0].dividers && gutters && gutters.left) {
      return true;
    }
    return false;
  }

  hasRightDivider(groups, gutters) {
    if (groups[1] && groups[1].dividers && gutters && gutters.right) {
      return true;
    }
    return false;
  }

  render() {
    const {contentHeight, groups, gutters, layoutMode, legendSpacing, width} =
      this.props;
    return (
      <Chart
        render={(chartProps) => {
          const {
            activeIndex,
            closestIndex,
            handleMove,
            handleOut,
            legendRows,
            setLegendRows,
            showTooltip,
          } = chartProps;

          const chartWidth = getChartWidth(width, this._node);
          const parsedGroups = getParsedGroups(groups);
          const guttersForGroups = this.getGuttersForGroups(
            gutters,
            parsedGroups,
          );
          const legendSeries = getFilteredSeries(groups, 'excludeFromLegend');
          const tooltipSeries = getFilteredSeries(groups, 'excludeFromTooltip');
          const hasHeader = this.hasHeader(guttersForGroups);

          const chartHeight =
            contentHeight + guttersForGroups.top * (legendRows + 1);

          if (this.state.height !== chartHeight) {
            setTimeout(() => {
              this.setState({height: chartHeight});
            });
          }

          const contentPadding = this.getContentPadding(
            parsedGroups,
            guttersForGroups,
          );
          const contentWidth = getContentWidth(
            chartWidth,
            guttersForGroups.left,
            guttersForGroups.right,
          );
          const contentWidthMinusPadding = this.getContentWidthMinusPadding(
            contentWidth,
            contentPadding,
          );
          const contentX = getContentX(guttersForGroups);
          const contentY = this.getContentY(
            hasHeader,
            legendRows,
            guttersForGroups.top,
            legendSpacing,
          );
          const contentTranslate = getTranslate(contentX, contentY);
          const numTicksX = getNumTicks(parsedGroups);
          const tickWidth = this.getTickWidth(
            contentWidthMinusPadding,
            numTicksX,
          );
          const maxItems = this.getMaxItems(legendSeries);
          const tickRange = this.getTickRange(
            layoutMode,
            tickWidth,
            maxItems,
            contentWidthMinusPadding,
          );
          const currentIndex = this.getCurrentIndex(
            activeIndex,
            closestIndex,
            layoutMode,
          );
          const tooltipX = this.getTooltipX(
            currentIndex,
            layoutMode,
            guttersForGroups,
            tickWidth,
            contentWidthMinusPadding,
            numTicksX,
            contentPadding,
          );

          return (
            <svg
              className={styles.TimeseriesChart}
              height={chartHeight}
              ref={(node) => (this._node = node)}
              width={chartWidth}>
              <StripeDefs />

              {
                // FIXME
                // A timeseries chart is only partially
                // rendered in iOS Safari until the user
                // touches it. After extensive experimentation,
                // it was discovered that adding a text
                // element after the opening svg tag
                // would circumvent this issue and get
                // the timeseries chart rendering correctly.
                // The issue is not present when the charts
                // are viewed separately; only when they
                // are viewed in a dashboard. So I suspect
                // that the root of the issue is something
                // outside charts, in the general
                // CSS. But that's a needle in a haystack,
                // and for now, this hack works.
              }
              <text style={{opacity: 0}}>{'#'}</text>

              <g
                className={styles.TimeseriesGroups}
                transform={contentTranslate}>
                {parsedGroups.map((datum, index) => {
                  const {
                    clampToZero,
                    dividers,
                    domainY,
                    end,
                    gridlines,
                    hideAxis,
                    label,
                    numTicksY,
                    series,
                    start,
                    style,
                    type,
                  } = datum;

                  return (
                    <TimeseriesGroup
                      activeIndex={currentIndex}
                      clampToZero={clampToZero}
                      contentPadding={contentPadding}
                      contentWidthMinusPadding={contentWidthMinusPadding}
                      contentY={contentY}
                      dividers={dividers}
                      domainY={domainY}
                      end={end}
                      gridlines={gridlines}
                      gutters={guttersForGroups}
                      height={contentHeight}
                      hideAxis={hideAxis}
                      index={index}
                      key={`timseriesGroup-${index}`}
                      label={label}
                      layoutMode={layoutMode}
                      legendHeight={guttersForGroups.top}
                      numTicksX={numTicksX}
                      numTicksY={numTicksY}
                      series={series}
                      start={start}
                      style={style}
                      tickWidth={tickWidth}
                      type={type}
                      width={contentWidth}
                    />
                  );
                })}
              </g>

              {guttersForGroups.top ? (
                <Legend
                  height={guttersForGroups.top}
                  series={legendSeries}
                  setLegendRows={setLegendRows}
                  width={contentWidth}
                />
              ) : null}

              <InteractionSurface
                handleMove={handleMove}
                handleOut={handleOut}
                height={contentHeight}
                tickRange={tickRange}
                width={
                  contentWidth - guttersForGroups.left - guttersForGroups.right
                }
                x={contentX + contentPadding.left}
                y={contentY}
              />

              {showTooltip ? (
                <Tooltip
                  activeIndex={currentIndex}
                  series={tooltipSeries}
                  x={tooltipX}
                  y={contentY}
                />
              ) : null}
            </svg>
          );
        }}
      />
    );
  }
}
